import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Stack,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { editTrackTH, editThState } from "../Redux/TrackSlice";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

function BootstrapDialogTitle(props) {
    const { children, onClose, ...other } = props;

    return (
        <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
            {children}
            {onClose ? (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <img src="/images/Home/close_img.png" alt="close" />
                </IconButton>
            ) : null}
        </DialogTitle>
    );
}

BootstrapDialogTitle.propTypes = {
    children: PropTypes.node,
    onClose: PropTypes.func.isRequired,
};

const ColorButton = styled(Button)(({ theme, disabled }) => ({
    color: disabled ? theme.palette.grey[600] : theme.palette.getContrastText("#7500C0"),
    backgroundColor: disabled ? theme.palette.grey[300] : "#7500C0",
    "&:hover": {
        backgroundColor: disabled ? theme.palette.grey[300] : "#7500C0",
        color: disabled ? theme.palette.grey[600] : "#fff",
    },
}));

function ManageTh(props) {
    const [localThState, setLocalThState] = useState([]);
    const [newThInput, setNewThInput] = useState("");
    const [isInputInvalid, setIsInputInvalid] = useState(false);
    const [isDuplicateTh, setIsDuplicateTh] = useState(false);
    const [duplicateEntries, setDuplicateEntries] = useState([]);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [confirmationType, setConfirmationType] = useState("");
    const [thConfirmationDetails, setThConfirmationDetails] = useState(null);

    const dispatch = useDispatch();

    useEffect(() => {
        if (props?.trackDetails?.th) {
            setLocalThState(props.trackDetails.th);
        }
    }, [props?.trackDetails?.th]);

    const handleClose = () => {
        props.setOpenEdit(false);
        setNewThInput("");
        setIsInputInvalid(false);
        setIsDuplicateTh(false);
        setDuplicateEntries([]);
    };

    const triggerAddConfirmation = () => {
        const inputList = newThInput
            .split(",")
            .map((id) => id.trim())
            .filter((id) => id.length > 0);

        if (inputList.length === 0) {
            setIsInputInvalid(true);
            return;
        }

        const existingIds = localThState.map((item) => item.enterpriseId.toLowerCase());

        const duplicates = inputList.filter((id) =>
            existingIds.includes(id.toLowerCase())
        );

        if (duplicates.length > 0) {
            setIsDuplicateTh(true);
            setDuplicateEntries(duplicates);
            return;
        }

        setThConfirmationDetails({
            track: props?.trackDetails?.id,
            newThList: inputList,
        });
        setConfirmationType("add");
        setConfirmationDialogOpen(true);
    };

    const handleConfirmationClose = () => {
        if (confirmationType === "stateChange" && thConfirmationDetails) {
            setLocalThState((prev) =>
                prev.map((item) =>
                    item.enterpriseId === thConfirmationDetails.eId
                        ? { ...item, isActive: thConfirmationDetails.previousState }
                        : item
                )
            );
        }
        setThConfirmationDetails(null);
        setConfirmationDialogOpen(false);
        setIsInputInvalid(false);
        setIsDuplicateTh(false);
        setDuplicateEntries([]);
    };

    const handleStateChange = (thId, enterpriseId, checked) => {
        const prevState =
            localThState.find((item) => item.enterpriseId === enterpriseId)?.isActive || 0;

        setLocalThState((prev) =>
            prev.map((item) =>
                item.enterpriseId === enterpriseId ? { ...item, isActive: checked ? 1 : 0 } : item
            )
        );

        setThConfirmationDetails({
            trackId: props?.trackDetails?.id,
            thId,
            eId: enterpriseId,
            state: checked ? 1 : 0,
            previousState: prevState,
        });
        setConfirmationType("stateChange");
        setConfirmationDialogOpen(true);
    };

    const handleConfirmAction = () => {
        if (confirmationType === "add" && thConfirmationDetails) {
            const newThEntries = thConfirmationDetails.newThList.map((id) => ({
                thId: Date.now() + Math.random(),
                enterpriseId: id,
                isActive: 1,
            }));

            setLocalThState((prev) => [...prev, ...newThEntries]);

            newThEntries.forEach((entry) => {
                dispatch(editTrackTH(thConfirmationDetails.track, entry.enterpriseId));
            });

            setNewThInput("");
        } else if (confirmationType === "stateChange" && thConfirmationDetails) {
            dispatch(editThState(thConfirmationDetails));
        }

        setThConfirmationDetails(null);
        setConfirmationDialogOpen(false);
        setIsInputInvalid(false);
        setIsDuplicateTh(false);
        setDuplicateEntries([]);
    };

    return (
        <div>
            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={props.openEdit}
                maxWidth="md"
                fullWidth
            >
                <BootstrapDialogTitle onClose={handleClose}>
                    <center>Manage TH</center>
                </BootstrapDialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ padding: "10px" }}>
                        <Typography sx={{ fontSize: "14px" }}>
                            Track Name: <b>{props?.trackDetails?.track}</b>
                        </Typography>

                        {/* TH Table */}
                        <TableContainer
                            component={Paper}
                            sx={{ maxHeight: 200, overflowY: "auto" }}
                        >
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#550A69" }}>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                color: "#fff",
                                                fontWeight: "bold",
                                                backgroundColor: "#550A69",
                                            }}
                                        >
                                            Enterprise ID
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                color: "#fff",
                                                fontWeight: "bold",
                                                backgroundColor: "#550A69",
                                            }}
                                        >
                                            State
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {localThState?.length > 0 ?
                                        localThState.map((item) => (
                                            <TableRow key={item.enterpriseId}>
                                                <TableCell align="center">{item.enterpriseId}</TableCell>
                                                <TableCell align="center">
                                                    <Switch
                                                        checked={item.isActive === 1}
                                                        onChange={(e) =>
                                                            handleStateChange(
                                                                item.thId,
                                                                item.enterpriseId,
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">No records found</TableCell>
                                            </TableRow>
                                        )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Add TH */}
                        <Stack spacing={1}>
                            <Typography sx={{ fontSize: "14px" }}>
                                Add New TH (comma separated){" "}
                                <span style={{ color: "red" }}>*</span>
                            </Typography>
                            <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                                <TextField
                                    error={isInputInvalid || isDuplicateTh}
                                    helperText={
                                        isInputInvalid
                                            ? "TH input cannot be empty."
                                            : isDuplicateTh
                                                ? `Duplicate TH(s): ${duplicateEntries.join(", ")}`
                                                : ""
                                    }
                                    value={newThInput}
                                    size="small"
                                    fullWidth
                                    multiline
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setNewThInput(value);
                                        setIsInputInvalid(false);

                                        const inputList = value
                                            .split(",")
                                            .map((id) => id.trim())
                                            .filter((id) => id.length > 0);

                                        const existingIds = localThState.map((item) =>
                                            item.enterpriseId.toLowerCase()
                                        );

                                        const duplicates = inputList.filter((id) =>
                                            existingIds.includes(id.toLowerCase())
                                        );

                                        if (duplicates.length > 0) {
                                            setIsDuplicateTh(true);
                                            setDuplicateEntries(duplicates);
                                        } else {
                                            setIsDuplicateTh(false);
                                            setDuplicateEntries([]);
                                        }
                                    }}
                                />
                                <ColorButton
                                    variant="contained"
                                    size="small"
                                    onClick={triggerAddConfirmation}
                                    disabled={
                                        newThInput.trim() === "" || isInputInvalid || isDuplicateTh
                                    }
                                    sx={{
                                        minWidth: "80px",
                                        height: "40px", // fixed height to prevent layout jump
                                    }}
                                >
                                    ADD
                                </ColorButton>
                            </Box>
                        </Stack>
                    </Stack>
                </DialogContent>
            </BootstrapDialog>

            {/* Confirmation Dialog */}
            <BootstrapDialog
                onClose={handleConfirmationClose}
                open={confirmationDialogOpen}
            >
                <BootstrapDialogTitle onClose={handleConfirmationClose}>
                    <center>Confirmation</center>
                </BootstrapDialogTitle>
                <DialogContent>
                    <Typography>
                        {confirmationType === "add" ? (
                            <>
                                Are you sure you want to add the following TH(s)?<br />
                                <strong>{thConfirmationDetails?.newThList?.join(", ")}</strong>
                            </>
                        ) : (
                            <>
                                Are you sure you want to change the state of TH -{" "}
                                <strong>{thConfirmationDetails?.eId}</strong> to{" "}
                                <strong>
                                    {thConfirmationDetails?.state === 1 ? "Active" : "Inactive"}
                                </strong>
                                ?
                            </>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center" }}>
                    <ColorButton
                        variant="outlined"
                        onClick={handleConfirmationClose}
                        sx={{ backgroundColor: "white", color: "#7500C0" }}
                    >
                        Cancel
                    </ColorButton>
                    <ColorButton variant="contained" onClick={handleConfirmAction}>
                        Yes
                    </ColorButton>
                </DialogActions>
            </BootstrapDialog>
        </div>
    );
}

export default ManageTh;
